import React, { useState, useContext, useEffect } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AppContext } from './App.js';
import { Users, Plus, Eye, DollarSign, Copy, CheckCircle, Trash2, Edit, TrendingUp, AlertCircle, ArrowLeft, History } from 'lucide-react';

const AffiliateAdminPage = () => {
    const { appInstance, showMessageBox } = useContext(AppContext);
    const [affiliates, setAffiliates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);

    // Load affiliates when page loads
    useEffect(() => {
        loadAffiliates();
    }, []);

    const loadAffiliates = async () => {
        try {
            const functions = getFunctions(appInstance);
            const listAffiliates = httpsCallable(functions, 'listAffiliates');
            const result = await listAffiliates();
            setAffiliates(result.data.affiliates || []);
        } catch (error) {
            console.error('Error loading affiliates:', error);
            showMessageBox('Error', 'Failed to load affiliates', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        // Navigate back to main admin or dashboard
        // Update this URL to match your app's routing
        window.location.href = '/?page=admin'; // or use your router
    };

    return (
        <div className="min-h-screen bg-gray-900 text-white p-6">
            <div className="max-w-6xl mx-auto">
                <Header 
                    onCreateNew={() => setShowCreateForm(true)} 
                    affiliates={affiliates}
                    onBack={handleBack}
                />
                
                {showCreateForm && (
                    <CreateAffiliateForm 
                        onClose={() => setShowCreateForm(false)}
                        onSuccess={() => {
                            setShowCreateForm(false);
                            loadAffiliates();
                        }}
                    />
                )}

                {loading ? (
                    <LoadingState />
                ) : (
                    <AffiliatesList 
                        affiliates={affiliates} 
                        onRefresh={loadAffiliates}
                    />
                )}
            </div>
        </div>
    );
};

// Header Component with Summary and Back Button
const Header = ({ onCreateNew, affiliates, onBack }) => {
    const totalEarnings = affiliates.reduce((sum, aff) => sum + (aff.totalEarnings || 0), 0);
    const totalUnpaid = affiliates.reduce((sum, aff) => sum + (aff.unpaidEarnings || 0), 0);
    const totalPaid = totalEarnings - totalUnpaid;
    const totalAffiliates = affiliates.length;

    return (
        <>
            {/* Back Button */}
            <button
                onClick={onBack}
                className="mb-4 flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
            >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Projects</span>
            </button>

            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-white flex items-center">
                        <Users className="w-8 h-8 mr-3" />
                        Affiliate Management
                    </h1>
                    <p className="text-gray-400 mt-2">Manage your affiliate partners and track performance</p>
                </div>
                <button
                    onClick={onCreateNew}
                    className="bg-purple-600 hover:bg-purple-700 px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors"
                >
                    <Plus className="w-5 h-5" />
                    <span>Add New Partner</span>
                </button>
            </div>

            {/* Summary Cards - Enhanced with Total Paid */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <div className="text-gray-400 text-sm">Total Partners</div>
                    <div className="text-2xl font-bold text-white mt-1">{totalAffiliates}</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                    <div className="text-gray-400 text-sm">Lifetime Earnings</div>
                    <div className="text-2xl font-bold text-green-400 mt-1">
                        ${(totalEarnings / 100).toFixed(2)}
                    </div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4 border border-blue-700 border-2">
                    <div className="text-gray-400 text-sm">Total Paid Out</div>
                    <div className="text-2xl font-bold text-blue-400 mt-1">
                        ${(totalPaid / 100).toFixed(2)}
                    </div>
                </div>
                <div className="bg-gray-800 rounded-lg p-4 border border-yellow-700 border-2">
                    <div className="text-gray-400 text-sm">Amount Owed</div>
                    <div className="text-2xl font-bold text-yellow-400 mt-1">
                        ${(totalUnpaid / 100).toFixed(2)}
                    </div>
                </div>
            </div>
        </>
    );
};

// Create Affiliate Form (unchanged)
const CreateAffiliateForm = ({ onClose, onSuccess }) => {
    const { appInstance, showMessageBox } = useContext(AppContext);
    const [formData, setFormData] = useState({
        partnerName: '',
        email: '',
        commissionRate: 10,
        promoCode: '',
        discountPercent: 25
    });
    const [isCreating, setIsCreating] = useState(false);

    // Auto-generate promo code when name changes
    useEffect(() => {
        if (formData.partnerName) {
            const cleanName = formData.partnerName
                .replace(/[^a-zA-Z0-9]/g, '')
                .toUpperCase()
                .substring(0, 10);
            setFormData(prev => ({
                ...prev,
                promoCode: `PARTNER-${cleanName}${prev.discountPercent}`
            }));
        }
    }, [formData.partnerName, formData.discountPercent]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsCreating(true);

        try {
            const functions = getFunctions(appInstance);
            const createAffiliate = httpsCallable(functions, 'createAffiliateComplete');
            
            const result = await createAffiliate({
                partnerName: formData.partnerName,
                email: formData.email,
                commissionRate: formData.commissionRate / 100,
                promoCode: formData.promoCode,
                discountPercent: formData.discountPercent
            });

            showMessageBox(
                'Success!', 
                `Affiliate created successfully!\n\nDashboard Link:\n${window.location.origin}?page=affiliate&id=${result.data.affiliateId}`,
                'success'
            );
            onSuccess();
        } catch (error) {
            console.error('Error creating affiliate:', error);
            showMessageBox('Error', `Failed to create affiliate: ${error.message}`, 'error');
        } finally {
            setIsCreating(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <h2 className="text-2xl font-bold text-white mb-6">Create New Affiliate Partner</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Partner Name *
                            </label>
                            <input
                                type="text"
                                required
                                value={formData.partnerName}
                                onChange={(e) => setFormData(prev => ({...prev, partnerName: e.target.value}))}
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                                placeholder="John Smith"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Email *
                            </label>
                            <input
                                type="email"
                                required
                                value={formData.email}
                                onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                                placeholder="john@example.com"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Commission Rate (%)
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="50"
                                value={formData.commissionRate}
                                onChange={(e) => setFormData(prev => ({...prev, commissionRate: parseInt(e.target.value)}))}
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Customer Discount (%)
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="50"
                                value={formData.discountPercent}
                                onChange={(e) => setFormData(prev => ({...prev, discountPercent: parseInt(e.target.value)}))}
                                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Promo Code (Auto-generated)
                        </label>
                        <input
                            type="text"
                            value={formData.promoCode}
                            onChange={(e) => setFormData(prev => ({...prev, promoCode: e.target.value}))}
                            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-purple-400 font-mono focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                        />
                    </div>

                    <div className="bg-purple-900 bg-opacity-30 border border-purple-700 rounded-lg p-4">
                        <p className="text-purple-300 text-sm">
                            <strong>Preview:</strong> Customer gets {formData.discountPercent}% off, you earn {formData.commissionRate}% commission on each sale.
                        </p>
                    </div>

                    <div className="flex space-x-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isCreating}
                            className="flex-1 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isCreating ? 'Creating...' : 'Create Affiliate'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Affiliates List Component with Payment History
const AffiliatesList = ({ affiliates, onRefresh }) => {
    const [copied, setCopied] = useState(null);
    const [paymentHistories, setPaymentHistories] = useState({});
    const { appInstance, showMessageBox } = useContext(AppContext);

    // Load payment history for each affiliate
    useEffect(() => {
        loadPaymentHistories();
    }, [affiliates]);

    const loadPaymentHistories = async () => {
        const functions = getFunctions(appInstance);
        const getPaymentHistory = httpsCallable(functions, 'getAffiliatePaymentHistory');
        
        const histories = {};
        for (const affiliate of affiliates) {
            try {
                const result = await getPaymentHistory({ affiliateId: affiliate.id });
                histories[affiliate.id] = result.data.payments || [];
            } catch (error) {
                console.error(`Error loading history for ${affiliate.id}:`, error);
                histories[affiliate.id] = [];
            }
        }
        setPaymentHistories(histories);
    };

    const copyDashboardLink = (affiliateId) => {
        const link = `${window.location.origin}?page=affiliate&id=${affiliateId}`;
        navigator.clipboard.writeText(link);
        setCopied(affiliateId);
        setTimeout(() => setCopied(null), 2000);
    };

    const copyPromoCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopied(code);
        setTimeout(() => setCopied(null), 2000);
    };

    const markAsPaid = async (affiliate) => {
        if (!affiliate.unpaidEarnings || affiliate.unpaidEarnings === 0) {
            showMessageBox('Info', 'No unpaid earnings to process', 'info');
            return;
        }

        if (!window.confirm(`Mark $${(affiliate.unpaidEarnings / 100).toFixed(2)} as paid for ${affiliate.name}?`)) {
            return;
        }

        try {
            const functions = getFunctions(appInstance);
            const markPayoutPaid = httpsCallable(functions, 'markAffiliatePayoutPaid');
            
            await markPayoutPaid({
                affiliateId: affiliate.id,
                amount: affiliate.unpaidEarnings
            });

            showMessageBox(
                'Success', 
                `✅ Marked $${(affiliate.unpaidEarnings / 100).toFixed(2)} as paid for ${affiliate.name}`, 
                'success'
            );
            
            // Refresh both affiliates and payment histories
            onRefresh();
            loadPaymentHistories();
        } catch (error) {
            console.error('Error marking payout:', error);
            showMessageBox('Error', `Failed to mark payout: ${error.message}`, 'error');
        }
    };

    if (affiliates.length === 0) {
        return (
            <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-medium text-gray-400 mb-2">No affiliates yet</h3>
                <p className="text-gray-500">Create your first affiliate partner to get started</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {affiliates.map((affiliate) => {
                const unpaidAmount = affiliate.unpaidEarnings || 0;
                const totalAmount = affiliate.totalEarnings || 0;
                const paidAmount = totalAmount - unpaidAmount;
                const hasUnpaid = unpaidAmount > 0;
                const paymentHistory = paymentHistories[affiliate.id] || [];

                return (
                    <div key={affiliate.id} className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="text-xl font-bold text-white">{affiliate.name}</h3>
                                <p className="text-gray-400">{affiliate.email}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                affiliate.status === 'active' ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-200'
                            }`}>
                                {affiliate.status}
                            </span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="bg-gray-700 rounded-lg p-3">
                                <div className="text-gray-400 text-sm">Commission Rate</div>
                                <div className="text-white font-semibold">{(affiliate.commissionRate * 100)}%</div>
                            </div>
                            <div className="bg-gray-700 rounded-lg p-3">
                                <div className="text-gray-400 text-sm">Total Conversions</div>
                                <div className="text-white font-semibold">{affiliate.totalConversions || 0}</div>
                            </div>
                        </div>

                        <div className="space-y-3 mb-4">
                            {affiliate.promoCodes?.map((code, index) => (
                                <div key={index} className="flex items-center justify-between bg-gray-700 rounded-lg p-3">
                                    <span className="font-mono text-purple-400">{code}</span>
                                    <button
                                        onClick={() => copyPromoCode(code)}
                                        className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors"
                                    >
                                        {copied === code ? (
                                            <CheckCircle className="w-4 h-4 text-green-400" />
                                        ) : (
                                            <Copy className="w-4 h-4" />
                                        )}
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Enhanced Earnings Display with Payment History */}
                        <div className="bg-gray-700 rounded-lg p-4 mb-3">
                            <div className="flex justify-between items-center mb-2">
                                <span className="text-gray-400 text-sm">Lifetime Earnings</span>
                                <span className="text-green-400 font-bold text-lg">
                                    ${(totalAmount / 100).toFixed(2)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-600">
                                <span className="text-gray-400 text-sm">Total Paid Out</span>
                                <span className="text-blue-400 font-bold text-lg">
                                    ${(paidAmount / 100).toFixed(2)}
                                </span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-gray-400 text-sm">Amount Owed</span>
                                <span className={`font-bold text-lg ${hasUnpaid ? 'text-yellow-400' : 'text-gray-500'}`}>
                                    ${(unpaidAmount / 100).toFixed(2)}
                                </span>
                            </div>
                        </div>

                        {/* Payment History Section */}
                        {paymentHistory.length > 0 && (
                            <div className="bg-gray-700 rounded-lg p-4 mb-3">
                                <div className="flex items-center space-x-2 mb-3">
                                    <History className="w-4 h-4 text-gray-400" />
                                    <span className="text-gray-300 text-sm font-semibold">Payment History</span>
                                </div>
                                <div className="space-y-2 max-h-32 overflow-y-auto">
                                    {paymentHistory.map((payment, index) => (
                                        <div key={index} className="flex justify-between items-center text-xs">
                                            <span className="text-gray-400">
                                                {new Date(payment.paidAt).toLocaleDateString()}
                                            </span>
                                            <span className="text-blue-400 font-semibold">
                                                ${(payment.amount / 100).toFixed(2)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Payout Section */}
                        {hasUnpaid && (
                            <div className="bg-yellow-900 bg-opacity-30 border border-yellow-700 rounded-lg p-3 mb-3">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <AlertCircle className="w-4 h-4 text-yellow-400" />
                                        <span className="text-yellow-300 text-sm font-medium">
                                            Pending Payout
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => markAsPaid(affiliate)}
                                        className="bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-sm text-white font-medium transition-colors"
                                    >
                                        Mark as Paid
                                    </button>
                                </div>
                            </div>
                        )}

                        <div className="flex space-x-2">
                            <button
                                onClick={() => copyDashboardLink(affiliate.id)}
                                className="flex-1 bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg text-sm transition-colors flex items-center justify-center space-x-2"
                            >
                                {copied === affiliate.id ? (
                                    <>
                                        <CheckCircle className="w-4 h-4" />
                                        <span>Copied!</span>
                                    </>
                                ) : (
                                    <>
                                        <Eye className="w-4 h-4" />
                                        <span>Copy Dashboard Link</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

// Loading State
const LoadingState = () => (
    <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-400">Loading affiliates...</span>
    </div>
);

export default AffiliateAdminPage;